import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ApiError } from '@/lib/api/client'
import type {
    ListExpenseCategoriesParams,
    UpsertExpenseCategoryRequest,
} from '@/lib/api/expense-category-api'
import { expenseCategoryApi } from '@/lib/api/expense-category-api'
import { m } from '@/lib/paraglide/messages'

// EXPENSE_CATEGORY_USED_BY_RULE carries `details.rule_count` — a dynamic
// count formatApiError's static code→message map can't express, so it is
// read here at the one call site that needs it.
function categoryDeleteErrorMessage(err: unknown): string {
    const apiErr = err as ApiError
    const details = apiErr?.details as { rule_count?: number } | undefined
    if (apiErr?.code === 'EXPENSE_CATEGORY_USED_BY_RULE' && details?.rule_count != null) {
        return m.expense_category_used_by_rule_error({
            count: String(details.rule_count),
        })
    }
    if (apiErr?.code === 'EXPENSE_CATEGORY_SYSTEM_LOCKED') {
        return m.expense_category_system_locked_hint()
    }
    return m.toast_expense_category_delete_error()
}

export const expenseCategoryKeys = {
    all: ['expense-categories'] as const,
    lists: () => [...expenseCategoryKeys.all, 'list'] as const,
    list: (params: ListExpenseCategoriesParams) =>
        [...expenseCategoryKeys.lists(), params] as const,
    detail: (id: string) => [...expenseCategoryKeys.all, 'detail', id] as const,
}

export function useExpenseCategories(params?: ListExpenseCategoriesParams) {
    return useQuery({
        queryKey: expenseCategoryKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await expenseCategoryApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useExpenseCategory(id: string) {
    return useQuery({
        queryKey: expenseCategoryKeys.detail(id),
        queryFn: async () => {
            const res = await expenseCategoryApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

export function useCreateExpenseCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertExpenseCategoryRequest) =>
            expenseCategoryApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseCategoryKeys.lists() })
            toast.success(m.toast_expense_category_save_success())
        },
        onError: () => toast.error(m.toast_expense_category_save_error()),
    })
}

export function useUpdateExpenseCategory(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertExpenseCategoryRequest) =>
            expenseCategoryApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseCategoryKeys.lists() })
            void qc.invalidateQueries({
                queryKey: expenseCategoryKeys.detail(id),
            })
            toast.success(m.toast_expense_category_save_success())
        },
        onError: (err) => {
            const apiErr = err as ApiError
            toast.error(
                apiErr?.code === 'EXPENSE_CATEGORY_SYSTEM_LOCKED'
                    ? m.expense_category_system_locked_hint()
                    : m.toast_expense_category_save_error(),
            )
        },
    })
}

export function useDeleteExpenseCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => expenseCategoryApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseCategoryKeys.lists() })
            toast.success(m.toast_expense_category_delete_success())
        },
        onError: (err) => toast.error(categoryDeleteErrorMessage(err)),
    })
}
